/**
 * useCmrForm.js
 * ─────────────────────────────────────────────────────────────
 * Encapsula toda la lógica del formulario del wizard CMR:
 *   - Estado inicial
 *   - Reducer con transiciones explícitas
 *   - Validaciones por paso
 *
 * Uso:
 *   const { state, dispatch, isPasoValid } = useCmrForm();
 *   const valido = isPasoValid(paso, catalogs.rutas.data);
 *
 * CAMBIO: changeType es ahora un array (MultiSelect).
 *   El payload de SET_CHANGE_RUTA es el array completo que devuelve e.value.
 */

import { useReducer, useCallback } from "react";
import { getIdsPorAccion, hasFrecuencia, needsEstructuras, showHorarios } from "./ChangeTypes";
import { esCorreoValido } from "../cmrShared";

/**
 * Convierte la fecha tentativa del backend a Date. Los registros viejos sin fecha
 * se guardaron como 01/01/1900 (fecha vacía de SQL Server): se tratan como sin capturar.
 */
const parsearFechaTentativa = (fecha) => {
    if (!fecha) return null;
    const date = new Date(fecha.replace('Z', ''));
    return isNaN(date) || date.getFullYear() <= 1900 ? null : date;
};

// ─── ESTADO INICIAL ───────────────────────────────────────────────────────────

const INITIAL_STATE = {
    clienteId               : null,
    ticketId                : null,
    rutaId                  : null,
    changeType              : [],   // ← array; uno o varios IDs del catálogo de cambios
    fechaTentativa          : null,
    estructurasSeleccionadas: [],
    // Solo se usa cuando frecuenciaModo === AMBAS: indica qué hacer con cada
    // estructura seleccionada → 'editar' | 'copia' | 'ambas'.
    // Si una estructura seleccionada no tiene entrada aquí, se asume 'ambas'
    // (mismo comportamiento que había antes de poder elegir por estructura).
    estructurasAccion       : {},
    nuevaUnidad             : null,
    modificaciones          : {},
    estructurasCopiadas     : [],
    // IDs de estructuras (origenId) cuya copia el usuario borró a propósito
    // (ícono de basura) mientras seguían marcadas para copiarse. Evita que
    // Paso2 la vuelva a generar sola. Se limpia cuando el usuario vuelve a
    // tocar esa estructura (des/re-seleccionarla o reclasificarla).
    copiasIgnoradas         : [],
    correo                  : '',
};

const crearCopia = (est) => ({
    _id         : crypto.randomUUID(),
    origenId    : est.id,
    nombre      : est.nombre + " (Copia)",
    turno       : est.turno ?? null,
    entrada     : Boolean(est.entrada),
    tipoServicio: est.tipoServicio,
    horaInicio  : est.horaInicio,
    horaFin     : est.horaFin,
    dias        : [...est.dias],
});

// ─── REDUCER ──────────────────────────────────────────────────────────────────

function formReducer(state, action) {
    switch (action.type) {

        // Cambiar cliente resetea todo lo que depende de él
        case "SET_CLIENTE":
            return { ...INITIAL_STATE, clienteId: action.payload };

        // Cambiar ticket resetea ruta hacia abajo
        case "SET_TICKET":
            return {
                ...state,
                ticketId                : action.payload,
                rutaId                  : null,
                changeType              : [],
                fechaTentativa          : null,
                estructurasSeleccionadas: [],
                estructurasAccion       : {},
                nuevaUnidad             : null,
                modificaciones          : {},
                estructurasCopiadas     : [],
                copiasIgnoradas         : [],
            };

        // Cambiar ruta resetea tipo de cambio y estructuras
        case "SET_RUTA":
            return {
                ...state,
                rutaId                  : action.payload,
                changeType              : [],
                fechaTentativa          : null,
                frecuenciaModo          : null,
                estructurasSeleccionadas: [],
                estructurasAccion       : {},
                nuevaUnidad             : null,
                modificaciones          : {},
                estructurasCopiadas     : [],
                copiasIgnoradas         : [],
            };

        case "SET_FECHA_TENTATIVA":
            return { ...state, fechaTentativa: action.payload };

        case "SET_CORREO":
            return { ...state, correo: action.payload };

        // Cambiar tipo(s) de cambio. Si se quita "Cambios en la frecuencia" se
        // descarta todo lo que dependía de ella (modo, acción por estructura,
        // copias y la selección de estructuras hecha para ese modo). Si ya ningún
        // tipo requiere estructuras, también se limpia la selección.
        case "SET_CHANGE_RUTA": {
            const changeType     = Array.isArray(action.payload) ? action.payload : [];
            const agregoFrec     = !hasFrecuencia(state.changeType) && hasFrecuencia(changeType);
            const quitoFrec      = hasFrecuencia(state.changeType) && !hasFrecuencia(changeType);
            const sinEstructuras = !needsEstructuras(changeType) && !hasFrecuencia(changeType);

            // Al agregar frecuencia el usuario debe elegir el modo explícitamente
            if (agregoFrec) return { ...state, changeType, frecuenciaModo: null };
            if (!quitoFrec && !sinEstructuras) return { ...state, changeType };

            return {
                ...state,
                changeType,
                frecuenciaModo          : hasFrecuencia(changeType) ? state.frecuenciaModo : null,
                estructurasSeleccionadas: [],
                estructurasAccion       : {},
                modificaciones          : {},
                estructurasCopiadas     : [],
                copiasIgnoradas         : [],
            };
        }

        // Re-seleccionar el mismo modo es un no-op (no toca nada).
        // Cambiar a un modo distinto descarta las copias previas: Paso2
        // las regenerará según corresponda al nuevo modo.
        case "SET_FRECUENCIA_MODO":
            if (state.frecuenciaModo === action.payload) return state;
            return {
                ...state,
                frecuenciaModo     : action.payload,
                estructurasCopiadas: [],
                copiasIgnoradas    : [],
            };

        // Toggle de una estructura (agrega o quita del array). Al tocarla se
        // limpia su "accion" y su marca de "ignorada" — es una decisión nueva
        // del usuario, así que Paso2 puede volver a generarle copia si aplica.
        case "TOGGLE_ESTRUCTURA": {
            const { estructurasSeleccionadas: sel, estructurasAccion, copiasIgnoradas } = state;
            const id = action.payload;
            const yaEstaba = sel.includes(id);
            const { [id]: _omit, ...accionRestante } = estructurasAccion;
            return {
                ...state,
                estructurasSeleccionadas: yaEstaba ? sel.filter((x) => x !== id) : [...sel, id],
                estructurasAccion       : yaEstaba ? accionRestante : estructurasAccion,
                copiasIgnoradas         : copiasIgnoradas.filter((x) => x !== id),
            };
        }

        // Reemplaza todo el array de estructuras (usado para seleccionar/deseleccionar todas)
        case "SET_ESTRUCTURAS":
            return {
                ...state,
                estructurasSeleccionadas: action.payload,
                estructurasAccion       : action.payload.length === 0 ? {} : state.estructurasAccion,
                copiasIgnoradas         : action.payload.length === 0 ? [] : state.copiasIgnoradas,
            };

        // Define qué hacer con una estructura puntual cuando frecuenciaModo === AMBAS:
        // 'editar' (solo modificar el original), 'copia' (solo generar copia nueva) o
        // 'ambas' (las dos cosas, comportamiento por defecto). Reclasificar es una
        // decisión explícita: se limpia "ignorada" para permitir que Paso2 genere
        // la copia si ahora corresponde.
        case "SET_ESTRUCTURA_ACCION":
            return {
                ...state,
                estructurasAccion: { ...state.estructurasAccion, [action.payload.id]: action.payload.accion },
                copiasIgnoradas  : state.copiasIgnoradas.filter((id) => id !== action.payload.id),
            };



        // Actualiza un campo de una estructura copiada
        case "UPDATE_ESTRUCTURA_COPIADA": {
            const { _id, field, value } = action.payload;
            return {
                ...state,
                estructurasCopiadas: state.estructurasCopiadas.map((e) =>
                    e._id === _id ? { ...e, [field]: value } : e
                ),
            };
        }

        // Elimina una copia porque el usuario la borró a propósito (ícono de basura).
        // Se recuerda en copiasIgnoradas para que Paso2 NO la regenere mientras la
        // estructura original siga marcada para copiarse.
        case "REMOVE_ESTRUCTURA_COPIADA": {
            const copia = state.estructurasCopiadas.find((e) => e._id === action.payload);
            return {
                ...state,
                estructurasCopiadas: state.estructurasCopiadas.filter((e) => e._id !== action.payload),
                copiasIgnoradas    : copia && !state.copiasIgnoradas.includes(copia.origenId)
                    ? [...state.copiasIgnoradas, copia.origenId]
                    : state.copiasIgnoradas,
            };
        }

        // Deja exactamente una copia por estructura marcada para copiarse.
        // Idempotente: dispararla varias veces no duplica nada.
        case "SINCRONIZAR_COPIAS": {
            const estructurasRuta   = action.payload ?? [];
            const { idsParaCopiar } = getIdsPorAccion(state);

            // Quita copias cuya estructura ya no se copia (deseleccionada o pasada a "editar")
            // y les pone el nombre de su estructura origen (las que vienen del backend en
            // modo edición traen un nombre genérico "Estructura copia <id>")
            const vigentes = state.estructurasCopiadas
                .filter((c) => idsParaCopiar.includes(c.origenId))
                .map((c) => {
                    const origen = estructurasRuta.find((e) => e.id === c.origenId);
                    return origen ? { ...c, nombre: origen.nombre + " (Copia)" } : c;
                });
            const conCopia = vigentes.map((c) => c.origenId);

            const nuevas = estructurasRuta
                .filter((e) =>
                    idsParaCopiar.includes(e.id) &&
                    !conCopia.includes(e.id) &&
                    !state.copiasIgnoradas.includes(e.id)
                )
                .map(crearCopia);

            return { ...state, estructurasCopiadas: [...vigentes, ...nuevas] };
        }

        case "SET_NUEVA_UNIDAD":
            return { ...state, nuevaUnidad: action.payload };

        // Actualiza un campo de una estructura específica
        case "UPDATE_MODIFICACION":
            return {
                ...state,
                modificaciones: {
                    ...state.modificaciones,
                    [action.estructuraId]: {
                        ...(state.modificaciones[action.estructuraId] ?? {}),
                        [action.field]: action.value,
                    },
                },
            };

        case "RESET":
            return INITIAL_STATE;

        // Pre-pobla el formulario completo desde un registro existente (modo edición).
        case "LOAD_COTIZACION": {
            // Normaliza las copias que vengan del backend para que siempre tengan `_id`
            // (necesario para editar/eliminar cada copia individualmente).
            const copiasCargadas = Array.isArray(action.payload.estructurasCopiadas)
                ? action.payload.estructurasCopiadas.map((c) => ({
                    ...c,
                    _id: c._id ?? crypto.randomUUID(),
                }))
                : [];
            return {
                ...INITIAL_STATE,
                ...action.payload,
                estructurasCopiadas: copiasCargadas,
                // El backend manda 'modificar' por defecto aunque no haya frecuencia
                frecuenciaModo: hasFrecuencia(action.payload.changeType) ? action.payload.frecuenciaModo : null,
                fechaTentativa: parsearFechaTentativa(action.payload.fechaTentativa),
            };
        }

        default:
            return state;
    }
}

// ─── VALIDACIONES ─────────────────────────────────────────────────────────────

/**
 * Paso 1:
 *   - cliente, ticket, ruta obligatorios
 *   - fecha tentativa y correo de notificación (con formato válido) obligatorios
 *   - al menos un tipo de cambio seleccionado
 *   - estructuras obligatorias SOLO si el tipo de cambio lo requiere
 *     (Cambio de unidad aplica a toda la ruta: no necesita estructuras)
 */
function validarPaso1(state) {
    const baseOk =
        Boolean(state.clienteId) &&
        Boolean(state.ticketId)  &&
        Boolean(state.rutaId)    &&
        Boolean(state.fechaTentativa) &&
        esCorreoValido(state.correo) &&
        state.changeType.length > 0;

    if (!baseOk) return false;

    // Si se eligió frecuencia, debe tener un modo seleccionado
    if (hasFrecuencia(state.changeType) && !state.frecuenciaModo) {
        return false;
    }

    // Determinar si se necesitan estructuras seleccionadas
    const needsEst = needsEstructuras(state.changeType);

    // Si frecuencia está activa con modo que lo requiere, también necesita estructuras
    const frecuenciaNeedsEst = hasFrecuencia(state.changeType) && state.frecuenciaModo;

    if (needsEst || frecuenciaNeedsEst) {
        return state.estructurasSeleccionadas.length > 0;
    }

    return true;
}

/**
 * Paso 2:
 *   - Si el cambio incluye horarios (directo, extensión o reducción),
 *     cada estructura seleccionada debe tener al menos un día operativo.
 *   - Si solo se eligió cambio de unidad o paradas, siempre es válido.
 *
 * @param {object} state
 * @param {Array}  rutasData – catálogo completo de rutas
 */
function validarPaso2(state, rutasData) {
    const showHorarioFields = showHorarios(state.changeType);
    const esFrecuencia      = hasFrecuencia(state.changeType);

    if (!showHorarioFields && !esFrecuencia) return true;

    const ruta = rutasData.find((r) => r.id === state.rutaId);
    if (!ruta) return false;

    const { idsAEditar, idsParaCopiar } = getIdsPorAccion(state);

    const horariosOk = idsAEditar.every((id) => {
        const original = ruta.estructuras.find((e) => e.id === id);
        const dias     = state.modificaciones?.[id]?.dias ?? original?.dias ?? [];
        return dias.length > 0;
    });
    if (!horariosOk) return false;

    // Solo exige copias si hay estructuras que deben copiarse y no fueron borradas a propósito
    const debeCopiar = idsParaCopiar.some((id) => !state.copiasIgnoradas.includes(id));
    if (debeCopiar && state.estructurasCopiadas.length === 0) return false;

    return state.estructurasCopiadas.every((c) => c.horaInicio && c.horaFin && c.dias.length > 0);
}

// ─── HOOK PÚBLICO ─────────────────────────────────────────────────────────────

export function useCmrForm() {
    const [state, dispatch] = useReducer(formReducer, INITIAL_STATE);

    /**
     * Devuelve true si el paso indicado cumple sus condiciones.
     * @param {number} paso       – número de paso actual (1, 2 o 3)
     * @param {Array}  rutasData  – catálogo de rutas (necesario solo para paso 2)
     */
    const isPasoValid = useCallback(
        (paso, rutasData = []) => {
            if (paso === 1) return validarPaso1(state);
            if (paso === 2) return validarPaso2(state, rutasData);
            return true; // paso 3 siempre válido (solo lectura)
        },
        [state]
    );

    return { state, dispatch, isPasoValid };
}
